import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import axios from "axios";
import {TFunction} from "i18next";
import {ParsedUrlQuery} from "querystring";
import React from "react";
import {buttonStyle} from "../../../common/constants/buttonStyle";
import {F006AmountIncreaseForm} from "../../../common/constants/pictureSettings";
import {SERVICES} from "../../../common/constants/routes";
import {enhancedFormData} from "../../../utility/backendRequestsUtil";
import {redirectTo} from "../../../utility/navigationUtil";
// Shared error-reporting helper; scheduled to move out of the F007 folder with SOSHDL-863.
import {logError} from "../F007ModularDonationForm/utils/submitUtils";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import CCOLink from "../../fundamentals/CCOLink";
import CCOResponsivePicture from "../../fundamentals/CCOResponsivePicture";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import {TextToggleButton} from "../../fundamentals/CCOTextToggleButton";
import CCOContainer from "../../fundamentals/CCOContainer";
import styles from "../../fundamentals/focus.module.css";
import {SosAmountIncreaseFormType} from "../../types";
import AlertBox from "../F007ModularDonationForm/sections/F007SubmitForm/AlertBox";
import {SubmitError} from "../F007ModularDonationForm/types";

//interfaces
export interface Props {
    id: string;
    sosAmountIncreaseForm: SosAmountIncreaseFormType;
    donationContext: DonationContext;
    csrfToken: string;
}

export interface DonationContext {
    actionParameters: any;
    amounts: Array<number>;
    dynamicAmounts_lowIncrease: Array<number>;
    dynamicAmounts_mediumIncrease: Array<number>;
    dynamicAmounts_highIncrease: Array<number>;
    errorMessage: string;
    mandatoryRequestParameters: string;
    minAmount_mediumIncrease: number;
    minAmount_highIncrease: number;
    optionalRequestParameters: string;
    privacyUrl: string;
}

export interface State {
    lobsterDataLoaded: boolean;
    lobsterDataError: boolean;
    freeAmount: string;
    purpose: string;
    amount: number;
    minAmount: number;
    maxAmount: number;
    amountFields: Array<TextToggleButton>;
    amountNew: number;
    sepaDate: string;
    lobsterData: {
        amountOld: number;
        paymentRhythmAmountOld: number;
        paymentMethod: string;
        paymentRhythmText: string;
        paymentRhythmKey: number;
    };
}

export type FormData = {
    CID: string;
    CampaignID: string;
    PSN: string;
    amountNew: string;
    donorType: string;
    formType: string;
    validFrom: string;
    donationUuid: string;
};

export type PeriodIncreaseFormData = Partial<FormData> & { isExtension: boolean };

// constants
// -> style

export const normalSubmitBtnStyle = {
    fontWeight: '700',
    fontFamily: 'var(--font-family-bold)',
    fontStyle: 'normal',
    fontSize: '18px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    borderRadius: '999px'
};
export const smallSubmitBtnStyle = {...normalSubmitBtnStyle, ...{width: '100%'}};

export const renderLoadingContainer = (state: Partial<State>,
                                       t: TFunction<"translation", undefined>) => {
    return <CCOContainer>
        <Grid size={{xs: 12, md: 12}}>
            <Typography align={"left"}
                        sx={{
                            fontSize: "1.125rem",
                            color: state.lobsterDataError ? "error.dark" : undefined
                        }}>
                {t(state.lobsterDataError ? "amount_increase_form.error_text" :
                       "amount_increase_form.loading_text")!}
            </Typography>
        </Grid>
    </CCOContainer>;
};

export const renderFormHeadlineAndTitle = (props: Props) => {
    return <>
        {props.sosAmountIncreaseForm.subheading1 && <Grid size={{xs: 12, md: 12}}>
            <CCOHeadline headline={props.sosAmountIncreaseForm.subheading1} type={"h2"}
                         justify={"left"}/>
        </Grid>}
        {props.sosAmountIncreaseForm.subheading2 && <Grid size={{xs: 12, md: 12}}>
            <CCOHeadline headline={props.sosAmountIncreaseForm.subheading2} type={"h3"}
                         justify={"left"}/>
        </Grid>}
    </>;
};

export const renderLobsterData = (lobsterData: any, props: Props, error: string,
                                  errorElement: JSX.Element, lowerThanWindowLarge: boolean,
                                  lowerThanWindowSmall: boolean) => {
    return <Grid data-cypress={'lobsterData'} size={{xs: 12, sm: 9, md: 9, lg: 7, xl: 8}}
                 style={lowerThanWindowLarge ? {paddingTop: "0px"} : {}}>
        <>{error && errorElement}</>
        {lobsterData &&
         replacePlaceholder(props, lobsterData, lowerThanWindowSmall)
             .map((richText: any, index: number) => <CCORichText key={index}
                                                                 textAsTree={richText}/>)}
    </Grid>;
};


export const renderPicture = (props: Props) => {
    return (
        <>
            {props.sosAmountIncreaseForm.picture ?
                <Grid data-cypress={'formPicture'} size={{xs: 12, md: 12}} sx={{
                    mb: {md: 8, xs: 1}
                }}>
                    <CCOResponsivePicture pictureSetting={F006AmountIncreaseForm}
                                          picture={props.sosAmountIncreaseForm.picture}
                                          imageStyle={{width: "100%", height: "auto"}}/>
                    {props.sosAmountIncreaseForm.picture &&
                     props.sosAmountIncreaseForm.picture.copyright ?
                        <Typography variant={'subtitle2'}
                                    sx={{marginTop: '12px'}}>{props.sosAmountIncreaseForm.picture?.copyright}</Typography> :
                        null}
                </Grid> : null
            }
        </>
    );
};

export const renderPrivacyInfo = (props: Props, t: TFunction<"translation", undefined>) => {
    return (
        <Grid data-cypress={'privacyInfo'} size={{xs: 12, md: 12}} sx={{display: "flex", paddingTop: "16px !important"}}>
            <Container
                sx={{
                    display: "flex",
                    marginTop: "7px",
                    marginBottom: "7px",
                    paddingLeft: "0px!important"
                }}>
                <Typography
                    sx={{
                        pr: 1,
                        display: "contents"
                    }}>{t("amount_increase_form.privacy_policy_text_begin")!}</Typography>
                <CCOLink displayContents customStyle={{display: "contents"}} noWrap={true}
                         target={props.donationContext.privacyUrl}
                         name={t("amount_increase_form.privacy_policy_link_text")!} noLinkIcon={true}
                         openInTab={true}/>
                <Typography
                    sx={{
                        pl: 1,
                        display: "contents"
                    }}>{t("amount_increase_form.privacy_policy_text_end")!}</Typography>
            </Container>
        </Grid>
    );
};

export const renderSubmitButton = (props: Props, lowerThanWindowLarge: boolean, focus: boolean,
                                   setFocus: (value: (((prevState: boolean) => boolean) | boolean)) => void,
                                   clicked: boolean, dataTestId: string,
                                   onSubmitClick?: () => void) => {
    return (
        <Grid size={{xs: 12, md: 12}} sx={{
            marginTop: 5
        }}>
            <Button type="submit"
                    sx={{
                        ...buttonStyle.green,
                        ...(lowerThanWindowLarge ? smallSubmitBtnStyle : normalSubmitBtnStyle)
                    }}
                    className={focus ? styles.rippleSbmt : ""}
                    onBlur={() => setFocus(false)}
                    onFocus={() => setFocus(true)}
                    size={"large"}
                    variant="contained"
                    disabled={clicked}
                    data-cypress={dataTestId}
                    data-testid={dataTestId}
                    onClick={onSubmitClick}
            >{props.sosAmountIncreaseForm.submitButtonText}</Button>
        </Grid>
    );
};

export const renderFormErrorArea = (errorBox: boolean,
                                    setErrorBox: (value: (((prevState: boolean) => boolean) | boolean)) => void,
                                    t: TFunction<"translation", undefined>) => {
    return <>
        <AlertBox
            open={errorBox}
            error={SubmitError.TWICE_SUBMITTED}
            translate={t}
            handleClose={() => setErrorBox(false)}
        />
    </>;
};

//functions

export function mandatoryParametersPresent(props: Props, parameterArray: ParsedUrlQuery) {
    const mandatoryCheck = (parameter: string) => parameterArray[parameter];
    return props.donationContext.mandatoryRequestParameters?.split(',').every(mandatoryCheck);
}

export function addRequestParameters(props: Props, requestData: any,
                                     parameterArray: ParsedUrlQuery) {
    props.donationContext.mandatoryRequestParameters?.split(',').forEach((parameter: any) => {
        parameterArray[parameter] &&
        (requestData[parameter] = encodeURI(parameterArray[parameter] as string));
    });
    props.donationContext.optionalRequestParameters?.split(',').forEach((parameter: any) => {
        parameterArray[parameter] &&
        (requestData[parameter] = encodeURI(parameterArray[parameter] as string));
    });
    Object.assign(requestData, props.donationContext.actionParameters!);
}

export function replacePlaceholder(props: Props, lobsterData: any, replaceTableInfo: boolean): any {
    const richTextsArray: any[] = [];

    if (lobsterData && lobsterData.responseData) {
        Object.values(lobsterData.responseData).map((responseItem: any) => {
            let replacedText = JSON.stringify(
                props.sosAmountIncreaseForm.lobsterDataTemplate!.textAsTree);
            if (replaceTableInfo) {
                replacedText = replacedText.replaceAll('"table"', '"div"');
                replacedText = replacedText.replaceAll('"tr"', '"div"');
                replacedText = replacedText.replaceAll('"td"', '"span"');
                replacedText = replacedText.replaceAll('"tbody"', '"div"');
            }
            Object.entries(responseItem).forEach(([key, value]) => {
                const keyPlaceholder = "${" + key + "}";
                replacedText = replacedText.replaceAll(keyPlaceholder, value as string);
            });
            const invalidParamsPattern = /\${(.+?)}/g;
            replacedText = replacedText.replaceAll(invalidParamsPattern, "");
            richTextsArray.push(JSON.parse(replacedText));
        });
    }
    return richTextsArray;
}

/**
 * Sends the increase amount confirmation.
 *
 * @param {any} formData - The form data to be sent.
 *
 * @param setClicked
 * @param setErrorBox
 * @param error
 * @param csrfToken
 * @param onSuccess
 * @param onError
 * @return {void}
 */
export function sendFormConfirmation(
    formData: FormData | PeriodIncreaseFormData,
    setClicked: any,
    setErrorBox: any,
    formName: string,
    csrfToken: string,
    onSuccess?: () => void | Promise<void>,
    onError?: (message?: string) => void | Promise<void>
): void {
    setClicked(true);
    axios({
        method: "post",
        url: SERVICES.AMOUNT_INCREASE,
        headers: {
            'X-CSRF-Token': csrfToken
        },
        data: enhancedFormData(formData, window.location.href)
    }).then(async function (success) {
        try {await onSuccess?.();} catch {}
        const forwardUrl = success.data.forwardUrl;
        if (forwardUrl != undefined) {
            redirectTo(forwardUrl);
        } else {
            logError(formName, 'submit succeeded but response contained no forwardUrl',
                     'submit').then();
        }
    }).catch(async function (error) {
        if (error.data != undefined && error.data.forwardUrl != undefined) {
            const forwardUrl = error.data.forwardUrl;
            redirectTo(forwardUrl);
        } else {
            setClicked(false);
            setErrorBox(true);
            await onError?.(error?.message);
            logError(formName, error?.message ?? 'unknown submit error', 'submit').then();
        }
    });
}

